import React, { useCallback, useMemo, useState } from 'react';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import { clinicToday } from '../../../utils/clinicTime';
import { formatDays } from '../../attendance/attendanceStatus';
import { kpiMap } from './kpis';

// What each person has left of each kind of leave this year. One row per person per type, which is
// what somebody approving leave actually needs to look up, and what a year-end reckoning needs.

const num = (v) => (v == null ? '-' : v);

const COLUMNS = [
  { key: 'userName', label: 'Name', wrap: true },
  { key: 'role', label: 'Role' },
  { key: 'typeName', label: 'Leave type' },
  {
    key: 'paid', label: 'Pay',
    render: (r) => (
      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.paid === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-800'}`}>
        {r.paid === false ? 'Unpaid' : 'Paid'}
      </span>
    ),
    csv: (r) => (r.paid === false ? 'Unpaid' : 'Paid'),
  },
  { key: 'quota', label: 'Days a year', align: 'right', render: (r) => (r.quota == null ? 'No limit' : r.quota), csv: (r) => r.quota },
  { key: 'used', label: 'Taken', align: 'right', render: (r) => num(r.used) },
  { key: 'pending', label: 'Waiting', align: 'right', render: (r) => num(r.pending) },
  {
    key: 'remaining', label: 'Left', align: 'right',
    render: (r) => (
      r.remaining == null
        ? <span className="text-gray-500">No limit</span>
        : <span className={`font-semibold ${r.remaining <= 0 ? 'text-red-700' : r.remaining <= 2 ? 'text-amber-700' : 'text-gray-900'}`}>{r.remaining}</span>
    ),
    csv: (r) => r.remaining,
  },
];

const thisYear = () => Number(clinicToday().slice(0, 4));

const LeaveBalancesReport = () => {
  const [year, setYear] = useState(thisYear);

  const fetcher = useCallback(() => reportsAPI.getLeaveBalances({ year }), [year]);
  const { data, loading } = useReport(fetcher);

  const rows = useMemo(() => data?.rows || [], [data]);
  const k = kpiMap(data);
  const finished = k.finished ?? rows.filter((r) => r.quota != null && r.remaining <= 0).length;
  const kpis = [
    { label: 'Staff', value: k.staff ?? new Set(rows.map((r) => r.userId)).size, sub: 'People with leave this year' },
    { label: 'Days taken', value: k.used ?? 0, sub: 'Approved leave in the year' },
    { label: 'Days waiting', value: k.pending ?? 0, sub: 'Requested, not yet decided' },
    { label: 'Unpaid days', value: k.unpaid ?? 0, sub: 'Taken as unpaid leave' },
    { label: 'Quotas finished', value: finished, sub: 'Person and type with nothing left', tone: finished > 0 ? 'alert' : undefined },
  ];

  const current = thisYear();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="bg-white rounded-2xl shadow p-4 flex items-center gap-2" role="group" aria-label="Year">
          {[current - 1, current, current + 1].map((y) => (
            <button
              key={y}
              type="button"
              aria-pressed={year === y}
              onClick={() => setYear(y)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${year === y ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {y}
            </button>
          ))}
        </div>
        <PrintButton />
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ReportTable
        title={`Leave left in ${year}`}
        columns={COLUMNS}
        rows={rows}
        csvName={`leave-balances-${year}`}
        emptyText="No leave types are set up yet"
        footnote={`Quotas run over the calendar year. "Taken" counts approved leave only; a request still waiting is shown separately and is not deducted until it is approved. ${formatDays(0.5)} is how a half day is counted.`}
        pageSize={25}
      />
    </div>
  );
};

export default LeaveBalancesReport;
